import { ArrowRight, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatCurrency } from '../../../../shared/lib/format'
import type { Plan } from './types'

/**
 * 요금제 카드.
 *
 * 한도(링크·클릭·팀 멤버)는 줄 단위로, 포함된 기능은 체크 목록으로 보여 준다.
 * 미포함 기능은 싣지 않는다 — 없는 것을 길게 늘어놓아 봐야 고르는 데 방해만
 * 된다. 백엔드가 미구현 기능을 이미 걸러 보낸다.
 */

interface PlanCardProps {
  plan: Plan
  onSelect: (plan: Plan) => void
  /**
   * 기능 목록을 두 열로 놓을지.
   * 카드 폭은 한 줄에 놓이는 플랜 수가 정한다. 좁은 카드에서 두 열로 두면
   * 한국어가 '월간 링 크 생성'처럼 끊겨 읽힌다.
   */
  twoColumnFeatures?: boolean
}

function limitText(value: number, unlimited: string): string {
  return value < 0 ? unlimited : value.toLocaleString()
}

export default function PlanCard({
  plan,
  onSelect,
  twoColumnFeatures = false,
}: PlanCardProps) {
  const { t } = useTranslation()
  const isFree = plan.price === 0
  const included = plan.features.filter(feature => feature.included)

  return (
    <div
      className={`relative flex flex-col gap-5 rounded-[20px] bg-white dark:bg-gray-800 ${
        plan.isPopular
          ? 'border-2 border-primary-600 p-[27px] shadow-[0_30px_60px_-30px_rgba(37,99,235,0.45)]'
          : 'border border-gray-200 p-7 dark:border-gray-700'
      }`}
    >
      {plan.isPopular && (
        <span className="absolute -top-3 left-7 rounded-full bg-primary-600 px-3 py-1 text-xs font-bold text-white">
          {t('pricing.recommended')}
        </span>
      )}

      <div className="flex flex-col gap-2.5">
        <span
          className={`text-[15px] font-bold ${
            plan.isPopular
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          {plan.name}
        </span>
        <div className="flex flex-wrap items-baseline gap-1.5">
          <span className="text-[40px] font-extrabold tabular-nums leading-none tracking-tight text-gray-900 dark:text-white">
            {isFree
              ? t('pricing.free')
              : formatCurrency(plan.price, plan.currency)}
          </span>
          {!isFree && (
            <span className="text-[15px] text-gray-500 dark:text-gray-400">
              /
              {plan.billingCycle === 'monthly'
                ? t('pricing.perMonth')
                : t('pricing.perYear')}
            </span>
          )}
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {plan.description}
        </span>
      </div>

      <button
        type="button"
        onClick={() => onSelect(plan)}
        className={`flex h-[46px] items-center justify-center gap-2 rounded-xl text-[15px] font-semibold transition ${
          plan.isPopular
            ? 'bg-primary-600 text-white hover:bg-primary-700'
            : 'border border-gray-200 text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700'
        }`}
      >
        {isFree ? t('pricing.startFree') : t('pricing.upgrade')}
        {!isFree && <ArrowRight className="h-4 w-4" />}
      </button>

      <div className="flex flex-col gap-3 border-t border-gray-100 pt-5 text-sm dark:border-gray-700">
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            {t('pricing.links')}
          </span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {limitText(plan.limits.links, t('pricing.unlimited'))}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            {t('pricing.clicks')}
          </span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {limitText(plan.limits.clicks, t('pricing.unlimited'))}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            {t('pricing.teamMembers')}
          </span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {limitText(plan.limits.teamMembers, t('pricing.unlimited'))}
          </span>
        </div>

        {included.length > 0 && (
          <div
            className={`mt-1 grid gap-x-4 gap-y-2.5 ${
              twoColumnFeatures ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
            }`}
          >
            {included.map(feature => (
              <span
                key={feature.name}
                className="flex items-start gap-2 text-gray-700 dark:text-gray-300"
              >
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
                <span className="leading-snug">{feature.name}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
